/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { removePreviousPrimaryContact } from 'common/utils/document-utils';
import { getClientOrderSettings } from 'common/services/OrderService';
import {
    DOCUMENT_TRADE_SUB_TYPE,
    DOCUMENT_TRADE_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';

const initialState = {
    initialized: false,
    quote: null,
    order: null,
    snapshot: null,
    finances: null,
};

export const createQuoteSlice = sliceName => {
    /**
     * Async thunk to update client and fetch client order settings
     */
    const updateClient = createAsyncThunk(
        `${sliceName}/updateClient`,
        async (clientId, { getState }) => {
            const state = getState();
            const currentClientId = state[sliceName].quote?.clientId;

            if (clientId !== currentClientId) {
                const { hasFulfillmentMethods, propertyRequired } =
                    await getClientOrderSettings(clientId);
                return {
                    clientId,
                    hasFulfillmentMethods,
                    propertyRequired,
                };
            }
            return null;
        }
    );

    const quoteSlice = createSlice({
        name: sliceName,
        initialState,
        reducers: {
            /**
             * Initializes the quote store with a quote object
             */
            init: (state, action) => {
                return {
                    ...state,
                    initialized: true,
                    quote: action.payload,
                };
            },

            /**
             * Replaces the current quote with a new quote
             */
            replace: (state, action) => {
                return {
                    ...state,
                    quote: action.payload,
                };
            },

            /**
             * Updates quote with provided updates object
             */
            update: (state, action) => {
                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        ...action.payload,
                    },
                };
            },

            /**
             * Updates quote property or properties
             * Payload: { key: string | string[], value: any | any[] }
             */
            updateKeyValuePair: (state, action) => {
                const { key, value } = action.payload;
                const update = {};

                if (Array.isArray(key)) {
                    key.forEach((k, idx) => {
                        update[k] = value[idx];
                    });
                } else {
                    update[key] = value;
                }

                return {
                    ...state,
                    quote: { ...state.quote, ...update },
                };
            },

            /**
             * Initializes financial information
             */
            initFinancialInformation: (state, action) => {
                return {
                    ...state,
                    finances: action.payload,
                };
            },

            /**
             * Updates payment information with order and snapshot
             */
            updatePayment: (state, action) => {
                const { order, snapshot } = action.payload;
                return {
                    ...state,
                    initialized: true,
                    order,
                    snapshot,
                };
            },

            /**
             * Updates billing information with order and snapshot
             */
            updateBilling: (state, action) => {
                const { order, snapshot } = action.payload;
                return {
                    ...state,
                    initialized: true,
                    order,
                    snapshot,
                };
            },

            /**
             * Updates quote instructions
             * Payload: { key: string | string[], value: any | any[] }
             */
            updateInstructions: (state, action) => {
                const { key, value } = action.payload;
                const update = {};

                if (Array.isArray(key)) {
                    key.forEach((k, idx) => {
                        update[k] = value[idx];
                    });
                } else {
                    update[key] = value;
                }

                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        instructions: { ...update },
                    },
                };
            },

            /**
             * Updates special instructions
             * Payload: string
             */
            updateSpecialInstructions: (state, action) => {
                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        instructions: {
                            special: action.payload,
                        },
                    },
                };
            },

            /**
             * Updates a contact by id
             * Payload: { key: string | string[], value: any | any[], id: string }
             */
            updateContact: (state, action) => {
                const { key, value, id } = action.payload;
                const update = {};
                let contacts = state.quote.contacts.filter(c => c.id !== id);

                if (Array.isArray(key)) {
                    key.forEach((k, idx) => {
                        if (k === 'primaryContact' && value[idx] === true) {
                            contacts = removePreviousPrimaryContact(
                                id,
                                state.quote.contacts,
                                contacts
                            );
                        }
                        update[k] = value[idx];
                    });
                } else {
                    if (key === 'primaryContact' && value === true) {
                        contacts = removePreviousPrimaryContact(
                            id,
                            state.quote.contacts,
                            contacts
                        );
                    }
                    update[key] = value;
                }

                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        contacts: [...contacts, update],
                    },
                };
            },

            /**
             * Deletes a contact by id
             * Payload: string (id)
             */
            deleteContact: (state, action) => {
                const contacts = state.quote.contacts.filter(
                    c => c.id !== action.payload
                );
                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        contacts,
                    },
                };
            },

            /**
             * Adds a new contact
             * Payload: object (contact)
             */
            addContact: (state, action) => {
                const contacts = [
                    ...(state.quote.contacts ?? []),
                    action.payload,
                ];

                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        contacts,
                    },
                };
            },

            /**
             * Removes a contact by uuid
             * Payload: string (uuid)
             */
            removeContact: (state, action) => {
                const contacts = [...state.quote.contacts];
                const idx = contacts.findIndex(c => c.uuid === action.payload);
                if (idx !== -1) {
                    contacts.splice(idx, 1);
                    return {
                        ...state,
                        quote: { ...state.quote, contacts },
                    };
                }
                return state;
            },

            /**
             * Adds a note to the job
             * Payload: object (note)
             */
            addNote: (state, action) => {
                const currentNotes = state.quote.job.notes || [];
                const notes = [...currentNotes, action.payload];

                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        job: { ...state.quote.job, notes },
                    },
                };
            },

            /**
             * Updates a note by id
             * Payload: object (note with id)
             */
            updateNote: (state, action) => {
                const notes = [...state.quote.job.notes];
                const idx = notes.findIndex(n => n.id === action.payload.id);

                if (idx !== -1) {
                    notes[idx] = action.payload;
                }

                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        job: { ...state.quote.job, notes },
                    },
                };
            },

            /**
             * Adds a comment to the quote
             * Payload: object (comment)
             */
            addComment: (state, action) => {
                const currentComments = state.quote.comments || [];
                const comments = [...currentComments, action.payload];

                return {
                    ...state,
                    quote: { ...state.quote, comments },
                };
            },

            /**
             * Updates a comment by id
             * Payload: object (comment with id)
             */
            updateComment: (state, action) => {
                const comments = [...state.quote.comments];
                const idx = comments.findIndex(c => c.id === action.payload.id);

                if (idx !== -1) {
                    comments[idx] = action.payload;
                }

                return {
                    ...state,
                    quote: { ...state.quote, comments },
                };
            },

            /**
             * Adds a photo to the quote
             * Payload: object (photo)
             */
            addPhoto: (state, action) => {
                const photos = state.quote.photos || [];
                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        photos: [...photos, action.payload],
                    },
                };
            },

            /**
             * Removes a photo by id
             * Payload: string (id)
             */
            removePhoto: (state, action) => {
                const photos = [...state.quote.photos];
                const idx = photos.findIndex(p => p.id === action.payload);
                if (idx !== -1) {
                    photos.splice(idx, 1);
                    return {
                        ...state,
                        quote: { ...state.quote, photos },
                    };
                }
                return state;
            },

            /**
             * Adds a folder attachment to the quote
             * Payload: object (folderAttachment)
             */
            addFolderAttachment: (state, action) => {
                const folderAttachments = [
                    ...(state.quote.folder?.folderAttachments || []),
                    action.payload,
                ];

                return {
                    ...state,
                    quote: {
                        ...state.quote,
                        folder: {
                            ...state.quote.folder,
                            folderAttachments,
                        },
                    },
                };
            },

            /**
             * Removes a folder attachment by id
             * Payload: string (id)
             */
            removeFolderAttachment: (state, action) => {
                const folderAttachments = [
                    ...state.quote.folder.folderAttachments,
                ];
                const idx = folderAttachments.findIndex(
                    fa => fa.id === action.payload
                );
                if (idx !== -1) {
                    folderAttachments.splice(idx, 1);
                    return {
                        ...state,
                        quote: {
                            ...state.quote,
                            folder: {
                                ...state.quote.folder,
                                folderAttachments,
                            },
                        },
                    };
                }
                return state;
            },

            /**
             * Resets initialization flag (e.g. when navigating to a different quote)
             */
            resetPages: state => {
                return {
                    ...state,
                    initialized: false,
                };
            },

            /**
             * Updates trade type with conditional logic for sub-types
             * Payload: string (tradeTypeId)
             */
            updateTradeType: (state, action) => {
                const tradeTypeId = action.payload;
                const update = {
                    tradeTypeId,
                    tradeSubTypeId: state.quote.tradeSubTypeId,
                };

                if (
                    tradeTypeId !== DOCUMENT_TRADE_TYPE.PLUMBING &&
                    state.quote.tradeSubTypeId ===
                        DOCUMENT_TRADE_SUB_TYPE.WATERHEATER
                ) {
                    update.tradeSubTypeId = DOCUMENT_TRADE_SUB_TYPE.RESIDENTIAL;
                }

                return {
                    ...state,
                    quote: { ...state.quote, ...update },
                };
            },
        },
        extraReducers: builder => {
            builder.addCase(updateClient.fulfilled, (state, action) => {
                if (action.payload) {
                    const {
                        clientId,
                        hasFulfillmentMethods,
                        propertyRequired,
                    } = action.payload;
                    const fulfillmentMethod = hasFulfillmentMethods
                        ? state.quote.fulfillmentMethod
                        : null;

                    return {
                        ...state,
                        quote: {
                            ...state.quote,
                            clientId,
                            hasFulfillmentMethods,
                            fulfillmentMethod,
                            propertyRequired,
                            propertyDisplay: null,
                            propertyId: null,
                        },
                    };
                }
                return state;
            });
        },
    });

    return {
        reducer: quoteSlice.reducer,
        actions: quoteSlice.actions,
        thunks: { updateClient },
    };
};
